import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, message, Button } from "antd";
import { createCommissionType, updateCommissionType, validateCommissionCode } from "../../../services/commission/commissionServices";
import { customBaseUrl, httpGet } from "../../../store/http";

const { TextArea } = Input;
const { Option } = Select;

const CommissionTypeModal = ({ visible, onCancel, onSuccess, initialValues }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [fetchingCategories, setFetchingCategories] = useState(false);
  const isEdit = !!initialValues;

  useEffect(() => {
    const fetchCategories = async () => {
        setFetchingCategories(true);
        try {
            const res = await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/commissions/categories`);
            if (res?.success) {
                setCategories(res.data);
            }
        } catch (err) {
            console.error("Error fetching categories", err);
        } finally {
            setFetchingCategories(false);
        }
    };

    if (visible) {
      fetchCategories();
      if (initialValues) {
        form.setFieldsValue({
          ...initialValues,
          commissionCategoryId: initialValues.commissionCategory?.id || initialValues.commissionCategoryId
        });
      } else {
        form.resetFields();
      }
    }
  }, [visible, initialValues, form]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        commissionCode: values.commissionCode,
        commissionName: values.commissionName,
        commissionCategoryId: values.commissionCategoryId,
        description: values.description || "",
        isActive: !!values.isActive,
      };

      let res;
      if (isEdit) {
        res = await updateCommissionType(initialValues.id, payload);
      } else {
        res = await createCommissionType(payload);
      }

      if (res?.success) {
        message.success(`Commission type ${isEdit ? "updated" : "created"} successfully`);
        onSuccess();
      } else {
        message.error(res?.message || `Failed to ${isEdit ? "update" : "create"} commission type`);
      }
    } catch (error) {
      message.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeBlur = async (e) => {
    const code = e.target.value;
    if (code && !isEdit) {
      try {
        const res = await validateCommissionCode(code);
        if (res?.success && !res.data) {
           // If res.data is false, it means it's NOT unique (or depends on API spec, 
           // usually validate-code returns true if valid/available)
           // If res.data is true, it means it exists/invalid. 
           // Let's assume the API returns { success: true, data: isAvailable }
           if (!res.data) {
             form.setFields([{ name: 'commissionCode', errors: ['This code is already in use'] }]);
           }
        }
      } catch (error) {
        console.error("Validation error", error);
      }
    }
  };

  return (
    <Modal
      title={isEdit ? "Edit Commission Type" : "Create Commission Type"}
      visible={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      destroyOnClose
      okText={isEdit ? "Update" : "Create"}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ isActive: true }}
      >
        <Form.Item
          name="commissionCode"
          label="Commission Code"
          rules={[{ required: true, message: "Please enter the commission code" }]}
        >
          <Input placeholder="e.g. TRF_01" onBlur={handleCodeBlur} disabled={isEdit} />
        </Form.Item>

        <Form.Item
          name="commissionName"
          label="Commission Name"
          rules={[{ required: true, message: "Please enter the commission name" }]}
        >
          <Input placeholder="e.g. Standard Transfer Commission" />
        </Form.Item>

        <Form.Item
          name="commissionCategoryId"
          label="Commission Category"
          rules={[{ required: true, message: "Please select a category" }]}
        >
          <Select placeholder="Select category" loading={fetchingCategories}>
            {categories.map(cat => (
              <Option key={cat.id} value={cat.id}>
                {cat.displayName}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
        >
          <TextArea rows={3} placeholder="Provide a brief description of this commission type" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CommissionTypeModal;
